// TODO: Add editor tip tap

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { EditorContent } from "@tiptap/react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { snackAdded } from "src/slices/system";
import LinkTooltip from "./LinkTooltip";

interface EditorProps {
  editor: any;
  isHumanizer?: boolean;
  isLoading?: boolean;
  title?: string;
  description?: string;
  wordCount?: number;
}

const Editor = ({
  editor,
  isHumanizer,
  isLoading,
  title,
  description,
  wordCount = 0,
}: EditorProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const [linkAnchorEl, setLinkAnchorEl] = useState<HTMLElement | null>(null);
  const [activeHref, setActiveHref] = useState<string>("");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  type Level = 1 | 2 | 3 | 4;
  // Modified link handlers with logging
  const handleLinkMouseEnter = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A") {
      setLinkAnchorEl(target);
      setActiveHref(target.getAttribute("href") || "");
      setIsTooltipOpen(true);
    }
  };

  const handleLinkMouseLeave = (e: Event) => {
    // Add a small delay to check if we're moving to the tooltip
    setTimeout(() => {
      const tooltipElement = document.querySelector(".MuiPopper-root");
      const isOverTooltip = tooltipElement?.matches(":hover");

      if (!isOverTooltip) {
        setIsTooltipOpen(false);
        setLinkAnchorEl(null);
        setActiveHref("");
      }
    }, 50);
  };

  const handleLinkEdit = (newHref: string) => {
    if (!editor) return;
    // Set the editor selection to encompass the link text
    const from = editor.view.posAtDOM(linkAnchorEl as HTMLElement, 0);
    const to = from + (linkAnchorEl as HTMLElement).textContent!.length;
    if (newHref) {
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .setLink({ href: newHref })
        .run();
    }
    dispatch(
      snackAdded({
        message: "Link updated successfully",
        type: "success",
      })
    );
  };

  const handleLinkDelete = () => {
    if (!editor) return;

    // Get the DOM node that contains the link
    const linkElement = linkAnchorEl as HTMLElement;
    if (!linkElement) return;

    // Set the editor selection to encompass the link text
    const from = editor.view.posAtDOM(linkElement, 0);
    const to = from + linkElement.textContent!.length;

    editor.chain().focus().setTextSelection({ from, to }).unsetLink().run();

    // Close the tooltip
    setIsTooltipOpen(false);
    setLinkAnchorEl(null);
    setActiveHref("");
    dispatch(
      snackAdded({
        message: "Link removed successfully",
        type: "success",
      })
    );
  };

  // Modified useEffect with better event handling
  useEffect(() => {
    if (!editor) return;

    const editorElement = editor.view.dom;
    editorElement.addEventListener("mouseenter", handleLinkMouseEnter, true);
    editorElement.addEventListener("mouseleave", handleLinkMouseLeave, true);

    return () => {
      editorElement.removeEventListener(
        "mouseenter",
        handleLinkMouseEnter,
        true
      );
      editorElement.removeEventListener(
        "mouseleave",
        handleLinkMouseLeave,
        true
      );
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: isHumanizer ? "calc(100vh - 180px)" : "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        position: "sticky",
      }}
    >
      <Card
        elevation={0}
        sx={{
          mt: 0,
          flex: 1,
          overflow: "auto",
          pl: "2rem",
          pr: "2rem",
          backgroundColor: "#FFFFFF",
          borderRadius: !isHumanizer ? 0 : 2,
          boxShadow: !isHumanizer ? "none" : "none",
          border: !isHumanizer ? "none" : `1px solid ${theme.palette.divider}`,
          position: "relative",

          "& .seowind-skeleton": {
            height: "20px !important",
            backgroundColor: "#f0f0f0 !important",
            borderRadius: "4px !important",
            margin: "8px 0 !important",
            color: "transparent !important",
            fontSize: "1px !important",
            lineHeight: "20px !important",
            overflow: "hidden !important",
            display: "block !important",
            "&::before": {
              display: "none !important", // Hide the 'P' indicator
            },
          },
          "& .seowind-skeleton-short": {
            height: "15px !important",
            backgroundColor: "#f0f0f0 !important",
            borderRadius: "4px !important",
            width: "65% !important",
            margin: "8px 0 24px 0 !important",
            color: "transparent !important",
            fontSize: "1px !important",
            lineHeight: "15px !important",
            overflow: "hidden !important",
            display: "block !important",
            "&::before": {
              display: "none !important",
            },
          },
          "& .seowind-unlock-card": {
            background:
              "linear-gradient(45deg, #5048E5, #EB1F8D, #5048E5) !important",
            borderRadius: "12px !important",
            padding: "24px !important",
            boxShadow:
              "0 12px 40px 0 rgba(80, 72, 229, 0.4), 0 4px 16px 0 rgba(0, 0, 0, 0.1) !important",
            borderLeft: "none !important",
            border: "none !important",
            margin: "20px 0 !important",
            textAlign: "center !important",
            display: "block !important",
            "& p, & span": {
              color: "white !important",
              fontFamily: "Inter, sans-serif !important",
              textAlign: "center !important",
              "&::before": {
                display: "none !important", // Hide the 'P' indicator
              },
            },
            "& .seowind-unlock-title": {
              fontSize: "14px !important",
              fontWeight: "600 !important",
              margin: "0 0 8px 0 !important",
            },
            "& .seowind-unlock-subtitle": {
              fontSize: "16px !important",
              fontWeight: "600 !important",
              margin: "0 0 16px 0 !important",
            },
            "& .seowind-unlock-button": {
              fontSize: "14px !important",
              fontWeight: "600 !important",
              background: "#EB1F8D !important",
              borderRadius: "8px !important",
              padding: "12px 24px !important",
              cursor: "pointer !important",
              display: "inline-block !important",
              border: "none !important",
              textDecoration: "none !important",
              transition: "transform 0.3s ease !important",
              "&:hover": {
                transform: "translateY(-2px) !important",
                boxShadow: "0 4px 20px rgba(235, 31, 141, 0.4) !important",
              },
            },
          },
          "& .ProseMirror": {
            outline: "none",
            minHeight: "100%",
            padding: "1rem 1rem 1.5rem",
            width: "100%",
            maxWidth: "930px",
            margin: "0 auto",
            "& .fact-check-underline": {
              textDecorationLine: "underline",
              textDecorationStyle: "solid",
              textDecorationThickness: "3px",
              textUnderlineOffset: "3px",
              cursor: "pointer",
            },
            "& .fact-check-critical": {
              textDecorationColor: "#EF4444",
              backgroundColor: "rgba(239, 68, 68, 0.18)",
            },
            "& .fact-check-warning": {
              textDecorationColor: "#F59E0B",
              backgroundColor: "rgba(245, 158, 11, 0.3)",
            },
            "& .fact-check-suggestion": {
              textDecorationColor: "#3B82F6",
              backgroundColor: "rgba(59, 130, 246, 0.18)",
            },
            a: {
              color: theme.palette.primary.main,
              textDecoration: "underline",
            },
            "& p": {
              marginBottom: "1.1em",
              lineHeight: 1.75,
              position: "relative",
              "&::before": {
                content: "'P'",
                color: theme.palette.grey[400],
                opacity: 0.75,
                fontSize: "0.68rem",
                fontFamily: "monospace",
                backgroundColor: theme.palette.grey[50],
                padding: "1px 4px",
                borderRadius: "2px",
                marginRight: "4px",
                userSelect: "none",
                position: "absolute",
                left: "-24px",
                top: "1.3em",
                transform: "translateY(-50%)",
              },
            },
            "& table": {
              borderCollapse: "collapse",
              margin: 0,
              overflow: "hidden",
              tableLayout: "fixed",
              width: "100%",
              border: `2px solid ${theme.palette.divider}`,
            },

            "& td, & th": {
              border: `2px solid ${theme.palette.divider}`,
              boxSizing: "border-box",
              minWidth: "1em",
              padding: "3px 5px",
              position: "relative",
              verticalAlign: "top",
            },

            "& td p, & th p": {
              "&::before": {
                display: "none !important",
                content: '""',
              },
              margin: "0 !important",
              position: "static !important",
              lineHeight: "inherit !important",
            },

            "& th": {
              backgroundColor: theme.palette.grey[100],
              fontWeight: "bold",
              textAlign: "left",
            },
            "& ul, & ol": {
              marginBottom: "1em",
              paddingLeft: "1.5em",
              "& li": {
                marginBottom: "0.5em",
                "& p::before": {
                  content: "none", // Remove the 'P' for paragraphs inside list items
                },
              },
            },
            "& h1": {
              marginBottom: "1em",
              lineHeight: 1.5,
              position: "relative",
              "&::before": {
                content: "'H1'",
                color: theme.palette.grey[500],
                fontSize: "0.75rem",
                fontFamily: "monospace",
                backgroundColor: theme.palette.grey[100],
                padding: "2px 4px",
                borderRadius: "2px",
                marginRight: "4px",
                userSelect: "none",
                position: "absolute",
                left: "-27px",
                top: "1.9em",
                transform: "translateY(-50%)",
              },
            },
            "& h2": {
              marginBottom: "1em",
              lineHeight: 1.7,
              position: "relative",
              "&::before": {
                content: "'H2'",
                color: theme.palette.grey[500],
                fontSize: "0.75rem",
                fontFamily: "monospace",
                backgroundColor: theme.palette.grey[100],
                padding: "2px 4px",
                borderRadius: "2px",
                marginRight: "4px",
                userSelect: "none",
                position: "absolute",
                left: "-27px", // Adjust as needed
                top: "50%",
                transform: "translateY(-50%)",
              },
            },
            "& h3": {
              marginBottom: "1em",
              lineHeight: 1.7,
              position: "relative",
              "&::before": {
                content: "'H3'",
                color: theme.palette.grey[500],
                fontSize: "0.75rem",
                fontFamily: "monospace",
                backgroundColor: theme.palette.grey[100],
                padding: "2px 4px",
                borderRadius: "2px",
                marginRight: "4px",
                userSelect: "none",
                position: "absolute",
                left: "-27px", // Adjust as needed
                top: "50%",
                transform: "translateY(-50%)",
              },
            },
            "& h4": {
              marginBottom: "1em",
              lineHeight: 1.7,
              position: "relative",
              "&::before": {
                content: "'H4'",
                color: theme.palette.grey[500],
                fontSize: "0.75rem",
                fontFamily: "monospace",
                backgroundColor: theme.palette.grey[100],
                padding: "2px 4px",
                borderRadius: "2px",
                marginRight: "4px",
                userSelect: "none",
                position: "absolute",
                left: "-27px", // Adjust as needed
                top: "50%",
                transform: "translateY(-50%)",
              },
            },
            "& .editor-link": {
              color: theme.palette.primary.main,
              textDecoration: "underline",
              cursor: "pointer",

              "&:hover": {
                textDecoration: "underline",
              },
            },
            "& blockquote": {
              borderLeft: `4px solid ${theme.palette.grey[500]}`,
              paddingLeft: "1rem",
              marginLeft: "1rem",
            },
            "& blockquote p": {
              marginBottom: "1em",
              lineHeight: 1.7,
              position: "relative",
              "&::before": {
                content: "none",
              },
            },
            "& code": {
              backgroundColor: "#1e293b",
              color: "#ffffff",
              fontFamily: "monospace",
              fontSize: "0.9rem",
              lineHeight: "1.7",
              display: "block !important",
              whiteSpace: "pre-wrap",
              padding: "1rem",
              borderRadius: "10px",
              margin: "1rem 0",
              width: "100%",
            },

            "& p code": {
              backgroundColor: "#1e293b",
              padding: "2px 4px",
              borderRadius: "2px",
              display: "inline",
              margin: 0,
            },
            "& code p": {
              marginBottom: "1em",
              lineHeight: 1.7,
              position: "relative",
              "&::before": {
                content: "none",
              },
            },
            "& blockquote code": {
              backgroundColor: "#1e293b",
              padding: "2px 4px",
              borderRadius: "2px",
            },
            "& a": {
              color: theme.palette.primary.main,
              textDecoration: "underline",
              cursor: "pointer",
              position: "relative",

              "&:hover": {
                textDecoration: "underline",
              },
            },
          },
          // "& p.is-editor-empty:first-child::before": {
          //   color: "var(--gray-4)",
          //   content: "attr(data-placeholder)",
          //   float: "left",
          //   left: "-27px",
          //   height: 0,
          //   pointerEvents: "none",
          // },
          "& .ProseMirror p.is-editor-empty:first-child::after": {
            color: "#adb5bd",
            content: "attr(data-placeholder)",
            float: "left",
            height: 0,
            mt: "-1.5em",
            pointerEvents: "none",
          },
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 2, mt: 2 }}>
            <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} width="60%" />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} width="60%" />
          </Box>
        ) : (
          <>
            {!isHumanizer && (
              <Accordion
                elevation={0}
                defaultExpanded
                onChange={(_: React.SyntheticEvent, expanded: boolean) =>
                  setIsExpanded(expanded)
                }
                sx={{
                  "&.MuiAccordion-root": {
                    border: "none",
                    "&:before": {
                      display: "none",
                    },
                  },
                  marginTop: "1.2rem",
                  // position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "white",
                }}
              >
                <AccordionSummary
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: "40px",
                    flexDirection: "column",
                    // padding: "0 16px",
                    margin: "0 !important",
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      justifyContent: "center",
                      flex: 1,
                      alignItems: "center",
                      margin: "0 !important",
                      padding: "0px !important",
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                      margin: "0 !important",
                      minHeight: 0,
                      padding: "0px !important",
                    },
                    "&.Mui-expanded": {
                      minHeight: 0,
                      margin: "0 !important",
                      padding: "0 !important",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-between",
                      paddingTop: "12px !important",
                    }}
                  >
                    <Card
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "1rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        width: "fit-content",
                        margin: "auto",
                        color: "#000000",
                      }}
                    >
                      Meta Information
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      />
                    </Card>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6">
                      <strong>Meta Title:</strong> {title}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Meta Description:</strong> {description}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Word Count:</strong> {wordCount.toLocaleString()} words
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {editor && <EditorContent editor={editor} />}
            {linkAnchorEl && (
              <LinkTooltip
                editor={editor}
                open={isTooltipOpen}
                anchorEl={linkAnchorEl}
                href={activeHref}
                onEdit={handleLinkEdit}
                onDelete={handleLinkDelete}
                onClose={() => {
                  setIsTooltipOpen(false);
                  setLinkAnchorEl(null);
                  setActiveHref("");
                }}
              />
            )}
          </>
        )}
      </Card>
    </Box>
  );
};

export default Editor;
